import React, { useState, useEffect } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import Input from "../../../../../../../../components/Form/Input";
import InputRange from "../../../../../../../../components/Form/InputRange";
import Select from "../../../../../../../../components/Form/Select";
import Modal from "../../../../../../../../components/Generic/Modal";
import { Measurement } from "../../../../../../../../models/measurement";
import { Status, MeasurementTypes } from "../../../../../../../../util/enums";
import useStyles from "./styles";

interface Props {
  measurement?: Measurement;
  open: boolean;
  hasError?: boolean;
  isLoading?: boolean;
  closeModal: () => void;
  submit: (measurement: any) => void;
}

const MeasurementModal = ({
  measurement,
  open,
  hasError,
  isLoading = false,
  closeModal,
  submit,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [description, setDescription] = useState<string>("");
  const [type, setType] = useState<string>(MeasurementTypes.DIMENSIONS);
  const [code, setCode] = useState<string>("");
  const [decimalPlaces, setDecimalPlaces] = useState<number>(0);
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    description: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (description) =>
        description ? description !== "" : false
      ),
    type: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (type) =>
        type ? type !== "" : false
      ),
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (code) =>
        code ? code !== "" : false
      ),
    decimalPlaces: yup.number().typeError(translate("thisFieldIsNumber")),
  });

  const clearFields = () => {
    setDescription("");
    setType("");
    setCode("");
    setStatus(Status.INACTIVE);
    setDecimalPlaces(0);
  };

  const isValid = (measurementValue: any) => {
    try {
      schema.validateSync(
        {
          code: measurementValue?.code,
          type: measurementValue?.type,
          description: measurementValue?.description,
          decimalPlaces: measurementValue?.decimalPlaces,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      code,
      description,
      type,
      status,
      decimalPlaces,
    };

    if (isValid(object)) submit(object);
  };

  useEffect(() => {
    if (measurement) {
      setCode(measurement?.code ?? "");
      setDescription(measurement?.description ?? "");
      setType(measurement?.type ?? "");
      setStatus(measurement?.status ?? "");
      setDecimalPlaces(measurement?.decimalPlaces ?? 0);
    }
  }, [measurement]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        code,
        description,
        type,
        decimalPlaces,
      };

      isValid(object);
    }
  }, [code, description, type, decimalPlaces, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="measurementModal"
      open={open}
      handleClose={closeModal}
      title={measurement ? translate("editMeasurement") : translate("createMeasurement")}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        loading: isLoading,
        saveDisabled: isLoading,
        textSave: measurement ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("measurementStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="code"
              label={translate("code")}
              placeholder={translate("code")}
              error={!!getErrorByField("code")}
              errorMessage={getErrorByField("code")}
              value={code}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="description"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("description")}
              errorMessage={getErrorByField("description")}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <InputRange
              containerClassName={classes.containerInputRange}
              disabled={isLoading}
              name="decimalPlaces"
              label={translate("decimalPoints")}
              placeholder={translate("decimalPlaces")}
              error={!!getErrorByField("decimalPlaces")}
              errorMessage={getErrorByField("decimalPlaces")}
              value={decimalPlaces}
              minValue={0}
              maxValue={10}
              type="number"
              onIncrease={setDecimalPlaces}
              onDecrease={setDecimalPlaces}
              change={(value) => {
                const nValue = Number(value);

                if (!Number.isNaN(nValue) && nValue <= 10) setDecimalPlaces(value);
              }}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Select
              disabled={isLoading}
              name="type"
              data-cy="measurementType"
              label={translate("type")}
              error={!!getErrorByField("type")}
              errorMessage={getErrorByField("type")}
              value={type}
              onChange={(e: SelectChangeEvent<any>) => setType(e.target.value)}
              placeholder={`${translate("select")}...`}
            >
              <MenuItem data-cy={MeasurementTypes.DIMENSIONS} value={MeasurementTypes.DIMENSIONS}>
                {MeasurementTypes.DIMENSIONS}
              </MenuItem>
              <MenuItem data-cy={MeasurementTypes.WEIGHT} value={MeasurementTypes.WEIGHT}>
                {MeasurementTypes.WEIGHT}
              </MenuItem>
            </Select>
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              name="measurementPreview"
              label={translate("measurementPreview")}
              rightIcon={<span>{code.trim() ? code.trim() : "--"}</span>}
              value={Number(1).toFixed(decimalPlaces)}
              disabled={true}
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default MeasurementModal;
